import React, { useState, useEffect, useRef } from 'react';
import { Image as KonvaImage } from 'react-konva';
import Konva from 'konva'; // Potrzebne dla Konva.Filters

export default function LoadableImage({
  shape,
  isSelected,
  onSelect,
  onChange,
  onRemoveBroken,
  onDragMove,
  onDragEnd,
  onLoadComplete,
  draggable = true,
  listening = true,
  publicMode = false
}) {
  // Zmieniamy nazwę stanu, aby było jasne, że może to być Image lub Canvas
  const [imageSource, setImageSource] = useState(null); 
  const imageRef = useRef(null);

  const locked = shape.flags?.locked ?? false;

  useEffect(() => {
    // console.log(`[LoadableImage] useEffect for ${shape.id}, src: ${shape.src}, image: ${shape.image}`);

    // Priorytet dla shape.image (pre-renderowany canvas dla SVG)
    if (shape.image && shape.image instanceof HTMLCanvasElement) {
      // console.log(`[LoadableImage] Using pre-rendered canvas for ${shape.id}`);
      setImageSource(shape.image);
      onLoadComplete?.();
      return; // Zakończ, jeśli użyliśmy canvas
    }

    // Fallback na shape.src (URL dla obrazów rastrowych lub jeśli canvas zawiódł)
    if (shape.src) {
      // console.log(`[LoadableImage] Loading image from src for ${shape.id}: ${shape.src}`);
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        // console.log(`[LoadableImage] Image loaded from src for ${shape.id}: ${img.naturalWidth}x${img.naturalHeight}`);
        setImageSource(img); // Użyj załadowanego obiektu Image
        onLoadComplete?.();
      };
      img.onerror = (err) => {
        console.error('[LoadableImage] ERROR loading src:', shape.src, err);
        setImageSource(null); // Wyczyść źródło w razie błędu
        onRemoveBroken?.(shape.id);
        onLoadComplete?.();
      };
      img.src = shape.src;
    } else {
      // Jeśli nie ma ani shape.image ani shape.src
      // console.log(`[LoadableImage] No image source (canvas or src) found for ${shape.id}`);
      setImageSource(null); // Ustaw źródło na null
      onLoadComplete?.();
    }
  // Zależności: reaguj na zmianę shape.image lub shape.src
  }, [shape.image, shape.src, shape.id, onRemoveBroken, onLoadComplete]);

  const handleClick = (e) => {
    if (publicMode && locked) {
      if (e?.evt) {
        e.evt.cancelBubble = true;
      }
      return;
    }
    onSelect?.(shape.id);
  };

  const handleDragMoveEvt = (e) => {
    if (!onDragMove) return;
    onDragMove(e);
  };

  const handleDragEndEvt = (e) => {
    const node = e.target;
    const finalX = node.x();
    const finalY = node.y();
    onChange?.({ id: shape.id, x: finalX, y: finalY });
    onDragEnd?.(e);
  };

  const handleTransformEnd = (e) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const rotation = node.rotation();
    const x = node.x();
    const y = node.y();

    // Dla SVG zapisujemy skalę, dla obrazów resetujemy i zapisujemy nowe wymiary
    if (shape._type === 'svg') {
        node.setAttrs({ scaleX: 1, scaleY: 1 }); // Reset skali węzła Konva
        onChange?.({ id: shape.id, x, y, rotation, scaleX: scaleX, scaleY: scaleY }); // Zapisujemy skalę w stanie
    } else {
        const newW = (shape.width || 100) * scaleX;
        const newH = (shape.height || 100) * scaleY;
        node.setAttrs({ width: newW, height: newH, scaleX: 1, scaleY: 1 });
        onChange?.({ id: shape.id, x, y, rotation, width: newW, height: newH });
    }
  };

  // Obsługa filtrów (np. kolorowanie SVG) - bez zmian
  let filtersArr = [];
  let filterProps = {};
  if (shape._type === 'svg' && shape.color && Array.isArray(shape.color)) {
    filtersArr = [Konva.Filters.RGBA];
    const [r, g, b] = shape.color;
    filterProps = {
      red: r,
      green: g,
      blue: b,
      alpha: 1
    };
  }

  useEffect(() => {
    if (!imageRef.current) return;
    if (filtersArr.length > 0) {
      imageRef.current.cache();
    } else {
      imageRef.current.clearCache();
    }
  }, [filtersArr, shape.color, imageSource]); // Dodano imageSource do zależności cache

  return (
    <KonvaImage
      ref={imageRef}
      id={shape.id}
      image={imageSource} // Używamy imageSource (może być Image lub Canvas)
      x={shape.x || 0}
      y={shape.y || 0}
      rotation={shape.rotation || 0}
      // Używamy docelowych wymiarów z obiektu shape
      width={shape.width || 100} 
      height={shape.height || 100}
      // Skala zewnętrzna jest stosowana przez Konva do KonvaImage
      scaleX={shape.scaleX || 1} 
      scaleY={shape.scaleY || 1} 
      draggable={draggable && !(publicMode && locked)}
      listening={listening && !(publicMode && locked)}
      onClick={handleClick}
      onTap={handleClick}
      onDragMove={handleDragMoveEvt}
      onDragEnd={handleDragEndEvt}
      onTransformEnd={handleTransformEnd}
      filters={filtersArr}
      {...filterProps}
      // Usunięto imageSmoothingEnabled={false}, aby użyć domyślnego wygładzania przeglądarki
    />
  );
}
